import dayjs from 'dayjs';
import Fuse from 'fuse.js';
import { IJobTitleState } from 'store/types';
import { ILabor, ILaborBreak, ILaborLocation, ILaborOverride, ILaborSchedule, IPOSLaborEmployee } from 'types';
import { formatDateAndTime, formatTime } from 'utils/date';

export const scheduleNeedsReview = (schedule: ILaborSchedule) => !schedule.end || schedule.breaks.some((laborBreak) => !laborBreak.end);

export const laborNeedsReview = (labor: ILabor) => labor.schedules.some(scheduleNeedsReview);

// Who made a manual override and when, e.g. "By Pat Manager · 09/15/2026 11:30 AM", or just when if the user has no name.
export const formatOverriddenBy = ({ byUser, at }: ILaborOverride) => {
  const name = byUser && `${byUser.firstName || ''} ${byUser.lastName || ''}`.trim();
  return `${name ? `By ${name} · ` : ''}${formatDateAndTime(at)}`;
};

// e.g. "Monday, September 14, 2026"
export const formatBusinessDate = (date: string) => dayjs(date).format('dddd, MMMM D, YYYY');

export const formatDuration = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${String(mins).padStart(2, '0')}m`;
};

export const getBreakDurationMinutes = (laborBreak: ILaborBreak) => (laborBreak.end ? dayjs(laborBreak.end).diff(dayjs(laborBreak.start), 'minute') : null);

export const getScheduleDurationMinutes = (schedule: ILaborSchedule) => {
  // Without a clock out or with a break still open, how long was worked isn't known.
  if (scheduleNeedsReview(schedule)) return null;
  const totalMinutes = dayjs(schedule.end).diff(dayjs(schedule.start), 'minute');
  const breakMinutes = schedule.breaks.reduce((sum, laborBreak) => sum + (getBreakDurationMinutes(laborBreak) || 0), 0);
  return Math.max(totalMinutes - breakMinutes, 0);
};

export const getEmployeeDurationLabel = (labor: ILabor) => {
  if (laborNeedsReview(labor)) return '—';
  const totalMinutes = labor.schedules.reduce((sum, schedule) => sum + (getScheduleDurationMinutes(schedule) || 0), 0);
  return formatDuration(totalMinutes);
};

interface IFormatBreaksListParams {
  schedule: ILaborSchedule;
  timezone: string;
}

export const formatBreaksList = ({ schedule, timezone }: IFormatBreaksListParams) => {
  if (schedule.breaks.length === 0) return 'No breaks';
  const breaksList = schedule.breaks
    .slice(0, 2)
    .map((laborBreak) => `${formatLocalTime({ isoInstant: laborBreak.start, timezone })} – ${laborBreak.end ? formatLocalTime({ isoInstant: laborBreak.end, timezone }) : 'Missing'}`)
    .join(', ');
  return schedule.breaks.length > 2 ? `${breaksList}, ...` : breaksList;
};

interface ILocalTimeParams {
  isoInstant: string;
  timezone: string;
}

export const toLocalTime = ({ isoInstant, timezone }: ILocalTimeParams) => dayjs.utc(isoInstant).tz(timezone).format('HH:mm');

export const formatLocalTime = ({ isoInstant, timezone }: ILocalTimeParams) => formatTime(toLocalTime({ isoInstant, timezone }), true);

// The longest reason a manager can give for changing or restoring labor, once trimmed.
export const REASON_MAX_LENGTH = 255;

export const REASON_TOO_LONG_MESSAGE = `Reason must be ${REASON_MAX_LENGTH} characters or fewer`;

// A complete wall-clock time, "HH:mm".
export const TIME_PATTERN = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;

const REFERENCE_DATE = '2000-01-01';

const toReferenceTime = (value: string) => dayjs(`${REFERENCE_DATE} ${value}`, 'YYYY-MM-DD HH:mm');

const toNextDate = (date: string) => dayjs(date).add(1, 'day').format('YYYY-MM-DD');

interface IZonedTimeParams {
  date: string;
  time: string;
  timezone: string;
}

const toZonedInstant = ({ date, time, timezone }: IZonedTimeParams) => dayjs.tz(`${date} ${time}`, 'YYYY-MM-DD HH:mm', timezone);

interface IToIsoInstantParams extends IZonedTimeParams {
  businessDayStart: string;
}

/**
 * Converts a wall-clock "HH:mm" time on business day `date` (in `timezone`) to a UTC ISO instant. The
 * business day runs from `businessDayStart` until that same time on the next calendar day, so a time
 * before `businessDayStart` (e.g. 01:00 when the day starts at 04:00) falls on the next calendar day, and
 * any other time on `date` itself.
 */
export const toIsoInstant = ({ date, time, timezone, businessDayStart }: IToIsoInstantParams) => {
  const isAfterMidnight = Boolean(businessDayStart) && toReferenceTime(time).isBefore(toReferenceTime(businessDayStart));
  const calendarDate = isAfterMidnight ? toNextDate(date) : date;
  return toZonedInstant({ date: calendarDate, time, timezone }).utc().toISOString();
};

interface IToIsoInstantAfterParams {
  time: string;
  timezone: string;
  after: string;
}

/**
 * Converts a wall-clock "HH:mm" time (in `timezone`) to its first occurrence at or after the ISO instant
 * `after`, e.g. a 02:00 clock-out after a 22:00 clock-in falls on the next day.
 */
export const toIsoInstantAfter = ({ time, timezone, after }: IToIsoInstantAfterParams) => {
  const afterDate = dayjs.utc(after).tz(timezone).format('YYYY-MM-DD');
  const sameDayInstant = toZonedInstant({ date: afterDate, time, timezone });
  const instant = sameDayInstant.isBefore(after) ? toZonedInstant({ date: toNextDate(afterDate), time, timezone }) : sameDayInstant;
  return instant.utc().toISOString();
};

interface IGetUniqueJobTitlesParams {
  laborRecords: ILabor[];
  jobs?: IJobTitleState[];
}

export const getUniqueJobTitles = ({ laborRecords, jobs = [] }: IGetUniqueJobTitlesParams) =>
  Array.from(new Set([...jobs.map((job) => job.title), ...laborRecords.flatMap((labor) => labor.schedules.map((schedule) => schedule.jobTitle))])).sort();

export interface ITimeRange {
  start: string;
  end: string;
}

const MINUTES_PER_DAY = 24 * 60;

// Minutes from wall-clock `start` forward to the next `end` ("HH:mm"), 0 when they read the same.
const minutesUntil = ({ start, end }: ITimeRange) => (toReferenceTime(end).diff(toReferenceTime(start), 'minute') + MINUTES_PER_DAY) % MINUTES_PER_DAY;

/**
 * Resolves wall-clock `start`..`end` ("HH:mm") against the same reference date, rolling `end` forward
 * a day when it isn't after `start`, so overnight ranges (e.g. 22:00 -> 02:00) resolve to the next day
 * the way `toIsoInstantAfter` saves them. With `after` ("HH:mm"), `start` is also rolled forward a day
 * when it reads earlier, the way `toLaborSchedule` places a break after its shift's clock in.
 */
const toReferenceInterval = ({ start, end }: ITimeRange, after?: string) => {
  let rangeStart = toReferenceTime(start);
  if (after && rangeStart.isBefore(toReferenceTime(after))) rangeStart = rangeStart.add(1, 'day');
  return { start: rangeStart, end: rangeStart.add(minutesUntil({ start, end }) || MINUTES_PER_DAY, 'minute') };
};

interface IIsTimeWithinRangeParams extends ITimeRange {
  time: string;
}

/**
 * Checks whether wall-clock time `time` ("HH:mm") falls within `start`..`end` ("HH:mm").
 */
export const isTimeWithinRange = ({ time, start, end }: IIsTimeWithinRangeParams) => {
  const { start: rangeStart, end: rangeEnd } = toReferenceInterval({ start, end });

  let target = toReferenceTime(time);
  if (target.isBefore(rangeStart)) target = target.add(1, 'day');

  return !target.isBefore(rangeStart) && !target.isAfter(rangeEnd);
};

interface IIsRangeWithinRangeParams {
  range: ITimeRange;
  within: ITimeRange;
}

/**
 * Checks whether `range` lies entirely within `within` (all "HH:mm"), resolving it the way `toLaborSchedule`
 * saves a break in its shift: `range.start` is its first occurrence at or after `within.start`, and
 * `range.end` its first occurrence at or after `range.start`. So a range that reads backwards (e.g. 12:30 ->
 * 12:00) runs almost a full day, and fails even when both of its times fall inside `within`.
 */
export const isRangeWithinRange = ({ range, within }: IIsRangeWithinRangeParams) => {
  const { start: withinStart, end: withinEnd } = toReferenceInterval(within);
  const rangeEndOffset = minutesUntil({ start: within.start, end: range.start }) + minutesUntil(range);
  return rangeEndOffset <= withinEnd.diff(withinStart, 'minute');
};

/**
 * Finds the first pair of overlapping "HH:mm" ranges (e.g. shifts, or breaks within a shift), mirroring
 * the backend's `doSegmentsOverlap` (see event_service's legion utils): ranges are resolved to the same
 * reference date, sorted by start, and adjacent ranges overlap when one starts before the running latest
 * end. Ranges that only touch (one ends exactly when the next starts) are not considered overlapping.
 * Pass `after` ("HH:mm", e.g. the shift's clock in for its breaks) to place each range at its first
 * occurrence after it, so ranges past midnight sort after the ones before it.
 * Returns every overlapping pair's positions in `ranges` (the running latest range first), or an empty array
 * when nothing overlaps.
 */
export const findOverlappingRanges = (ranges: ITimeRange[], after?: string): [number, number][] => {
  if (ranges.length < 2) return [];

  const sorted = ranges.map((range, index) => ({ index, ...toReferenceInterval(range, after) })).sort((a, b) => a.start.valueOf() - b.start.valueOf() || a.end.valueOf() - b.end.valueOf());

  const overlaps: [number, number][] = [];
  let latest = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i];
    if (current.start.isBefore(latest.end)) overlaps.push([latest.index, current.index]);
    if (current.end.isAfter(latest.end)) latest = current;
  }

  return overlaps;
};

interface IIsTimeOutsideShiftParams {
  time: string;
  shift: ITimeRange;
}

// Whether a break time falls outside its shift. Only decidable once the time and both ends of the shift are set.
export const isTimeOutsideShift = ({ time, shift }: IIsTimeOutsideShiftParams) => Boolean(time && shift.start && shift.end) && !isTimeWithinRange({ time, start: shift.start, end: shift.end });

interface IIsBreakOutsideShiftParams {
  laborBreak: ITimeRange;
  shift: ITimeRange;
}

// Whether either break time falls outside its shift, or (once all four times are complete) the break as a whole
// doesn't fit: both times can be inside the shift while the break reads backwards (e.g. 12:30 -> 12:00), which
// would be saved as a break running into the next day.
const isBreakOutsideShift = ({ laborBreak, shift }: IIsBreakOutsideShiftParams) => {
  if (isTimeOutsideShift({ time: laborBreak.start, shift }) || isTimeOutsideShift({ time: laborBreak.end, shift })) return true;

  const isComplete = [laborBreak.start, laborBreak.end, shift.start, shift.end].every((time) => TIME_PATTERN.test(time || ''));
  return isComplete && !isRangeWithinRange({ range: laborBreak, within: shift });
};

export interface IShiftTimes extends ITimeRange {
  breaks: ITimeRange[];
}

export interface IBreakOutsideShift {
  index: number;
  message: string;
}

/**
 * Finds every break that doesn't fit its shift (all "HH:mm"), with the message to show on each: a break time
 * outside the shift names the shift's range, while a break whose times are both inside but reads backwards asks
 * for its end to come after its start. Returns an empty array until the shift has both a clock in and a clock
 * out, or when every break fits.
 */
export const findBreaksOutsideShift = (shift: IShiftTimes): IBreakOutsideShift[] => {
  if (!shift?.start || !shift?.end) return [];

  const rangeLabel = `${formatTime(shift.start, true)} – ${formatTime(shift.end, true)}`;
  return shift.breaks.flatMap((laborBreak, index) => {
    if (!isBreakOutsideShift({ laborBreak, shift })) return [];

    const hasTimeOutsideShift = isTimeOutsideShift({ time: laborBreak.start, shift }) || isTimeOutsideShift({ time: laborBreak.end, shift });
    return [{ index, message: hasTimeOutsideShift ? `Break is outside ${rangeLabel}` : 'Break end must be after break start' }];
  });
};

/**
 * Finds every break that overlaps one starting before it among a shift's breaks (all "HH:mm"), skipping breaks
 * missing a time. Anchored on the clock in, so in an overnight shift the breaks after midnight sort after the
 * ones before it. Returns those breaks' indexes in `shift.breaks`, or an empty array when no breaks overlap.
 */
export const findOverlappingBreaks = (shift: Pick<IShiftTimes, 'start' | 'breaks'>): number[] => {
  const completeBreaks = shift.breaks.map((laborBreak, originalIndex) => ({ ...laborBreak, originalIndex })).filter((laborBreak) => laborBreak.start && laborBreak.end);

  const clockIn = TIME_PATTERN.test(shift.start || '') ? shift.start : undefined;
  return findOverlappingRanges(completeBreaks, clockIn).map(([, laterPosition]) => completeBreaks[laterPosition].originalIndex);
};

export interface ILaborReviewEmployee {
  employeeId: string;
  name?: string;
  // The location of the employee's labor record, or the primary one when Legion sent no labor for this employee.
  location: ILaborLocation;
  // Absent when Legion sent no labor for this employee.
  labor?: ILabor;
}

interface IJoinLaborWithEmployeesParams {
  laborRecords: ILabor[];
  posEmployees: IPOSLaborEmployee[];
  // The primary location first.
  locations: ILaborLocation[];
}

/**
 * Every labor record from Legion, plus every employee on the POS roster without one, sorted by name. An employee
 * with labor at more than one of the site's locations appears once per location. Employees without a POS name
 * (labor records with no matching POS employee) are sorted last.
 *
 * Every employee comes with a location, since their shifts can't be shown or saved without its timezone and business
 * day: a record whose location isn't among `locations` is left out, and so is the roster when there are no locations.
 * Callers are expected to refuse to show such labor rather than rely on this.
 */
export const joinLaborWithEmployees = ({ laborRecords, posEmployees, locations }: IJoinLaborWithEmployeesParams): ILaborReviewEmployee[] => {
  const locationsById = new Map(locations.map((location) => [location.id, location]));
  const namesByEmployeeId = new Map(posEmployees.map((employee) => [employee.id, employee.name]));
  const employeeIdsWithLabor = new Set(laborRecords.map((labor) => labor.employeeId));
  const primaryLocation = locations[0];
  const employees: ILaborReviewEmployee[] = [
    ...laborRecords.filter((labor) => locationsById.has(labor.locationId)).map((labor) => ({ employeeId: labor.employeeId, name: namesByEmployeeId.get(labor.employeeId), location: locationsById.get(labor.locationId), labor })),
    ...(primaryLocation
      ? Array.from(namesByEmployeeId)
          .filter(([employeeId]) => !employeeIdsWithLabor.has(employeeId))
          .map(([employeeId, name]) => ({ employeeId, name, location: primaryLocation }))
      : []),
  ];

  const named = employees.filter((employee) => employee.name).sort((a, b) => a.name.localeCompare(b.name));
  return [...named, ...employees.filter((employee) => !employee.name)];
};

// Identifies an employee's labor at one location, e.g. as a React key or a tooltip ID. Each ID is URI-encoded, so the
// separator can't appear inside either one and ("a-b", "c") never collides with ("a", "b-c").
export const getEmployeeLocationKey = (locationId: string, employeeId: string) => `${encodeURIComponent(locationId)}/${encodeURIComponent(employeeId)}`;

// An override that removed every shift still counts as labor data: it's how the employee gets excluded from the tip calculation.
export const hasLaborData = ({ labor }: ILaborReviewEmployee) => Boolean(labor?.schedules.length || labor?.overridden);

interface ISearchEmployeesParams {
  employees: ILaborReviewEmployee[];
  query: string;
}

// Fuzzy match on the employee name, prefix match on the employee ID. Keeps the employees' order.
export const searchEmployees = ({ employees, query }: ISearchEmployeesParams) => {
  const nameMatches = new Set(new Fuse(employees, { keys: ['name'], threshold: 0.2, ignoreLocation: true }).search(query).map((result) => result.item));
  return employees.filter((employee) => employee.employeeId.startsWith(query.trim()) || nameMatches.has(employee));
};
